import { Alert, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { CancellationServicePolicy, CancellationServiceRule } from '@bandido/shared/types';
import { formatMoney } from '../money';

export function CancellationServicesPanel({ policy, currency }: { policy: CancellationServicePolicy; currency?: string }) {
  const { t } = useTranslation();
  const prefix = 'travelServices.detail';
  const boundary = (value: string) => value.replace('T', ' ');
  const rule = (row: CancellationServiceRule, index: number) => (
    <Typography variant="body2" key={index}>
      {t(`${prefix}.serviceRuleFrom`, { from: boundary(row.from) })}
      {row.utcOffset ? ` (UTC${row.utcOffset})` : ''}: {formatMoney({ amountMinor: row.feeMinor, currency: row.currency })}
    </Typography>
  );
  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Typography variant="subtitle2">{t(`${prefix}.serviceRulesTitle`)}</Typography>
      {policy.source && <Typography variant="caption">{t(`${prefix}.policySource.${policy.source}`)}</Typography>}
      <Typography variant="caption">{t(`${prefix}.serviceRulesNotice`)}</Typography>
      {policy.scopeStatus && policy.scopeStatus !== 'complete' && (
        <Alert severity="warning">{t(`${prefix}.policyScope.${policy.scopeStatus}`)}</Alert>
      )}
      {policy.services?.map((service, index) => (
        <Box key={service.serviceId}>
          <Typography variant="body2" fontWeight={600}>{t(`${prefix}.policyService`, { number: index + 1 })}</Typography>
          {service.rules.length ? service.rules.map(rule) : <Typography variant="body2">{t(`${prefix}.serviceRulesMissing`)}</Typography>}
        </Box>
      ))}
      {policy.unattributedRules?.map(rule)}
      {!!policy.rulesSummary?.length && currency && (
        <Box>
          <Alert severity="info">{t(`${prefix}.policySummaryBasis`)}</Alert>
          {policy.rulesSummary.map((row, index, rows) => (
            <Typography variant="body2" key={index}>
              {row.from ? t(`${prefix}.serviceRuleFrom`, { from: boundary(row.from) }) : t(`${prefix}.policyBeforeFirst`)}
              {rows[index + 1]?.from ? ` · ${t(`${prefix}.serviceRuleUntil`, { to: boundary(rows[index + 1].from!) })}` : ''}
              : {formatMoney({ amountMinor: row.feeMinor, currency })}{row.feePercent !== undefined ? ` (${row.feePercent}%)` : ''}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}
